<template>
  <div
    :class="[
      'field-group',
      { disabled: disabled, error: error, 'with-sub-label': hasSecondLabel }
    ]"
    @keyup.enter.prevent="toggleCheckboxValue"
  >
    <checkbox-base
      :name="name"
      :label="label"
      :is-checked="isChecked"
      :disabled="disabled"
      @change="toggleCheckboxValue"
    >
      <template v-if="showSlot">
        <span class="checkbox-label">
          <span class="main-label">
            <slot name="main-label" />
          </span>

          <span
            v-if="hasSecondLabel"
            class="sub-label"
          >
            <slot name="second-label" />
          </span>
        </span>
      </template>
    </checkbox-base>
    <errors
      v-if="error && errorMsg"
      :errors="[errorMsg]"
    />
  </div>
</template>

<script>
  import CheckboxBase from 'components/Elements/Base/Checkbox'
  export default {
    name: 'Checkbox',
    components: {
      CheckboxBase,
      Errors: () =>
        import(/* webpackChunkName: "element-errors" */ 'components/Elements/Errors')
    },
    props: {
      label: {
        type: String,
        default: ' '
      },
      name: {
        type: String,
        required: true
      },
      isChecked: {
        type: Boolean,
        default: false,
        required: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      error: {
        type: Boolean,
        default: false,
        required: false
      },
      errorMsg: {
        type: String,
        default: '',
        required: false
      },
      showSlot: {
        type: Boolean,
        default: false,
        required: false
      }
    },
    computed: {
      hasSecondLabel() {
        return Object.prototype.hasOwnProperty.call(this.$slots, 'second-label')
      }
    },
    methods: {
      toggleCheckboxValue() {
        this.$emit('update', !this.isChecked)
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .field-group {
    margin-bottom: 18px;
    display: block;

    &.error {
      border-color: $red-error;
    }

    &.with-sub-label {
      .checkbox-label {
        line-height: 18px;
        font-size: em(14);

        .main-label {
          font-weight: 500;
          font-size: em(12);
        }

        .sub-label {
          color: $grey;
          font-size: em(12);
        }
      }
    }

    ::v-deep .dot {
      width: 3px;
      height: 8px;
      position: relative;
      margin: 0 2px;
      display: inline-block;

      &:before {
        content: '';
        width: 3px;
        height: 3px;
        display: flex;
        border-radius: 50%;
        background-color: $grey-default;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .checkbox-label {
      user-select: none;
      display: flex;
      flex-direction: column;
      cursor: pointer;
      font-size: em(14);
      line-height: 24px;
      width: 100%;
    }
  }

  ::v-deep input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;

    + .checkbox-label {
      position: relative;
      display: inline-block;
      padding-left: 40px;
      color: $blue-dark;
      font-size: em(14);
      letter-spacing: 0;
      line-height: 20px;

      &:before {
        @include filled-icon-font;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        width: 24px;
        height: 24px;
        border: 1px solid $blue-medium;
        border-radius: 4px;
        background-color: white;
        line-height: 24px;
        box-sizing: border-box;
      }
    }

    &:focus {
      + .checkbox-label:before {
        border-color: #b7b8be;
      }
    }

    &:disabled {
      + .checkbox-label:before {
        background-color: $blue-disabled;
        color: $blue-secondary;
        border-color: $blue-disabled;
      }
    }

    &:checked {
      + .checkbox-label:before {
        content: $filled-icon-check;
        background-color: $blue-medium;
        color: white;
        padding: 0 5px 0 4px;
      }

      &:disabled {
        + .checkbox-label:before {
          content: $filled-icon-check;
          background-color: $blue-disabled;
          color: $blue-secondary;
          border: none;
          padding: 0 5px 0 4px;
        }
      }
    }
  }
</style>
